/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import FormBox from '../common/FormBox/FormBox';
import { useEffect, useState } from 'react';
import api from 'api';
import { Autocomplete } from '@mui/material';
import BaseInput from 'components/common/BaseInput';
import { useCountries } from 'hooks/useReferenceData';
import * as S from '../forms/Forms.styled';
import ReactGA from 'react-ga4';

export interface Data {
    account_holder: string;
    account_holder_address: string;
    account_holder_city: string;
    account_holder_country: string;
    account_holder_post_code: string;
    bank_name: string;
    bank_address: string;
    bank_city: string;
    bank_country: string;
    bank_post_code: string;
    account_number: string;
    swift: string;
    iban: string;
}

export const initBankAccount: Data = {
    account_holder: '',
    account_holder_address: '',
    account_holder_city: '',
    account_holder_country: '',
    account_holder_post_code: '',
    bank_name: '',
    bank_address: '',
    bank_city: '',
    bank_country: '',
    bank_post_code: '',
    account_number: '',
    swift: '',
    iban: '',
};

interface ValidationMsg {
    account_holder: string;
    account_holder_address: string;
    account_holder_city: string;
    account_holder_country: string;
    account_holder_post_code: string;
    bank_name: string;
    bank_address: string;
    bank_city: string;
    bank_country: string;
    bank_post_code: string;
    account_number: string;
    swift: string;
    iban: string;
}

const initMsg: ValidationMsg = {
    account_holder: '',
    account_holder_address: '',
    account_holder_city: '',
    account_holder_country: '',
    account_holder_post_code: '',
    bank_name: '',
    bank_address: '',
    bank_city: '',
    bank_country: '',
    bank_post_code: '',
    account_number: '',
    swift: '',
    iban: '',
};

export default function BankAccount() {
    const tableName = 'BankAccount';
    const [data, setData] = useState<Data>(initBankAccount);
    const [validationMsg, setValidationMsg] = useState<ValidationMsg>(initMsg);
    const { data: countryOptions } = useCountries();

    useEffect(() => {
        ReactGA.send({
            hitType: 'pageview',
            page: `${window.location.pathname}`,
            title: 'BankAccount',
        });
    }, []);

    useEffect(() => {
        api.tableInput.newFetchCandidateInput(setData, tableName);
    }, []);

    const handleUpdate = (field: string, value: string) => {
        validate(field, value);
        const tmp: Data = { ...data, [field]: value };
        setData(tmp);
    };

    // FIELD VALIDATION
    const validate = (field: string, value: string) => {
        let validated = true;
        let tmp: ValidationMsg = validationMsg;
        if (!field && !value) {
            for (const [key, value] of Object.entries(data)) {
                let msg = '';
                if (value === '') {
                    msg = 'Field should not be left empty';
                    validated = false;
                    tmp = { ...tmp, [key]: msg };
                }
            }
        } else {
            let msg = '';
            if (value === '') {
                msg = 'Field should not be left empty';
                validated = false;
            }
            tmp = { ...tmp, [field]: msg };
        }
        setValidationMsg(tmp);
        return validated;
    };

    return (
        <FormBox validation={validate} tableNames={[tableName]} data={[data]}>
            <S.Title>Bank account details</S.Title>
            <div className={`mb-8`} />
            <S.SubTitle>Account Holder Information</S.SubTitle>
            <S.GridSection>
                <S.FlexColumn>
                    <BaseInput
                        // sx={{
                        //     width: 356,
                        // }}
                        id="account_holder"
                        label="Account holder name"
                        type="text"
                        value={data.account_holder}
                        error={validationMsg.account_holder !== ''}
                        onFocus={(e) => {
                            validate('account_holder', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('account_holder', e.target.value);
                        }}
                        helperText={validationMsg.account_holder}
                    />
                </S.FlexColumn>
                <S.FlexColumn>
                    <BaseInput
                        // sx={{
                        //     width: 356,
                        // }}
                        id="account_holder_address"
                        label="Address"
                        type="text"
                        value={data.account_holder_address}
                        error={validationMsg.account_holder_address !== ''}
                        onFocus={(e) => {
                            validate('account_holder_address', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('account_holder_address', e.target.value);
                        }}
                        helperText={validationMsg.account_holder_address}
                    />
                </S.FlexColumn>
                <S.FlexColumn>
                    <BaseInput
                        // sx={{
                        //     width: 356,
                        // }}
                        id="account_holder_city"
                        label="City"
                        type="text"
                        value={data.account_holder_city}
                        error={validationMsg.account_holder_city !== ''}
                        onFocus={(e) => {
                            validate('account_holder_city', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('account_holder_city', e.target.value);
                        }}
                        helperText={validationMsg.account_holder_city}
                    />
                </S.FlexColumn>
                <S.FlexColumn>
                    <BaseInput
                        // sx={{
                        //     width: 356,
                        // }}
                        id="account_holder_post_code"
                        label="Postal Code"
                        type="text"
                        value={data.account_holder_post_code}
                        error={validationMsg.account_holder_post_code !== ''}
                        onFocus={(e) => {
                            validate('account_holder_post_code', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('account_holder_post_code', e.target.value);
                        }}
                        helperText={validationMsg.account_holder_post_code}
                    />
                </S.FlexColumn>
                <S.FlexColumn>
                    <Autocomplete
                        sx={{
                            '& .MuiInputBase-input': {
                                height: 30,
                            },
                        }}
                        disablePortal
                        id="country"
                        value={data.account_holder_country}
                        options={countryOptions ?? []}
                        onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                            validate('account_holder_country', e.target.value);
                        }}
                        onChange={(_, v: any) => handleUpdate('account_holder_country', v)}
                        renderInput={(params) => (
                            <BaseInput
                                {...params}
                                label="Country"
                                error={validationMsg.account_holder_country !== ''}
                                helperText={validationMsg.account_holder_country}
                            />
                        )}
                    />
                    {/* <span style={{ color: "red" }}>{validationMsg.account_holder_country}</span> */}
                </S.FlexColumn>
            </S.GridSection>
            <div className={`flex items-center gap-x-2.5`}>
                <p>Bank Information</p>
            </div>
            <S.GridSection>
                <S.FlexColumn>
                    <BaseInput
                        id="bank_name"
                        label="Bank Name"
                        type="text"
                        value={data.bank_name}
                        error={validationMsg.bank_name !== ''}
                        onFocus={(e) => {
                            validate('bank_name', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('bank_name', e.target.value);
                        }}
                        helperText={validationMsg.bank_name}
                    />
                </S.FlexColumn>
                <S.FlexColumn>
                    <BaseInput
                        // sx={{
                        //     width: 356,
                        // }}
                        id="bank_address"
                        label="Address"
                        type="text"
                        value={data.bank_address}
                        error={validationMsg.bank_address !== ''}
                        onFocus={(e) => {
                            validate('bank_address', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('bank_address', e.target.value);
                        }}
                        helperText={validationMsg.bank_address}
                    />
                </S.FlexColumn>
                <S.FlexColumn>
                    <BaseInput
                        // sx={{
                        //     width: 356,
                        // }}
                        id="bank_city"
                        label="City"
                        type="text"
                        value={data.bank_city}
                        error={validationMsg.bank_city !== ''}
                        onFocus={(e) => {
                            validate('bank_city', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('bank_city', e.target.value);
                        }}
                        helperText={validationMsg.bank_city}
                    />
                </S.FlexColumn>
                <S.FlexColumn>
                    <BaseInput
                        // sx={{
                        //     width: 356,
                        // }}
                        id="bank_post_code"
                        label="Postal Code"
                        type="text"
                        value={data.bank_post_code}
                        error={validationMsg.bank_post_code !== ''}
                        onFocus={(e) => {
                            validate('bank_post_code', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('bank_post_code', e.target.value);
                        }}
                        helperText={validationMsg.bank_post_code}
                    />
                </S.FlexColumn>
                <S.FlexColumn>
                    <Autocomplete
                        sx={{
                            '& .MuiInputBase-input': {
                                height: 30,
                            },
                        }}
                        disablePortal
                        id="country"
                        value={data.bank_country}
                        options={countryOptions ?? []}
                        onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                            validate('bank_country', e.target.value);
                        }}
                        onChange={(_, v: any) => handleUpdate('bank_country', v)}
                        renderInput={(params) => (
                            <BaseInput
                                {...params}
                                label="Country"
                                error={validationMsg.bank_country !== ''}
                                helperText={validationMsg.bank_country}
                            />
                        )}
                    />
                    {/* <span style={{ color: "red" }}>{validationMsg.account_holder_country}</span> */}
                </S.FlexColumn>
            </S.GridSection>

            <div className={`flex items-center gap-x-2.5`}>
                <p>Account Information</p>
            </div>
            <S.GridSection>
                <S.FlexColumn>
                    <BaseInput
                        id="account_number"
                        label="Account Number"
                        type="text"
                        value={data.account_number}
                        error={validationMsg.account_number !== ''}
                        onFocus={(e) => {
                            validate('account_number', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('account_number', e.target.value);
                        }}
                        helperText={validationMsg.account_number}
                    />
                </S.FlexColumn>
                <S.FlexColumn>
                    <BaseInput
                        id="swift"
                        label="SWIFT"
                        type="text"
                        value={data.swift}
                        error={validationMsg.swift !== ''}
                        onFocus={(e) => {
                            validate('swift', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('swift', e.target.value);
                        }}
                        helperText={validationMsg.swift}
                    />
                </S.FlexColumn>
                <S.FlexColumn>
                    <BaseInput
                        id="iban"
                        label="IBAN"
                        type="text"
                        value={data.iban}
                        error={validationMsg.iban !== ''}
                        onFocus={(e) => {
                            validate('iban', e.target.value);
                        }}
                        onChange={(e) => {
                            handleUpdate('iban', e.target.value);
                        }}
                        helperText={validationMsg.iban}
                    />
                </S.FlexColumn>
            </S.GridSection>
        </FormBox>
    );
}
